import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getApps } from 'firebase/app';
import { Analytics, getAnalytics, isSupported, logEvent, setUserId } from 'firebase/analytics';
import mixpanel from 'mixpanel-browser';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Funnel events. Mixpanel is the primary sink; a subset is mirrored to GA4
 * (see GA4_EVENT_NAMES). Keep names stable: Mixpanel reports key on them.
 */
export type AnalyticsEvent =
  | 'Signed Up'
  | 'Logged In'
  | 'Subscribe Started'
  | 'Subscribed'
  | 'Subscription Cancel Started'
  | 'Subscription Cancelled'
  | 'Job Created'
  | 'Job Completed'
  | 'Job Failed'
  | 'Job Failed by Client'
  | 'Job Stopped'
  | 'Job Retry Started'
  | 'Upload Validation Error'
  | 'Results Exported'
  | 'Late Swap Used';

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

/** GA4 mirror names (recommended events where one exists). */
const GA4_EVENT_NAMES: Partial<Record<AnalyticsEvent, string>> = {
  'Signed Up': 'sign_up',
  'Logged In': 'login',
  'Subscribe Started': 'begin_checkout',
  'Subscribed': 'purchase',
  'Subscription Cancelled': 'subscription_cancelled',
  'Job Created': 'job_created',
  'Job Completed': 'job_completed',
  'Job Failed': 'job_failed',
  'Results Exported': 'results_exported',
};

const TRACK_ONCE_KEY = 'aceAnalyticsOnce';
const TRACK_ONCE_MAX = 300;

/**
 * Thin wrapper over mixpanel-browser and Firebase Analytics (GA4).
 * Every method is a safe no-op when the token / measurement id is missing
 * or the SDK throws, so call sites can be one-liners.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private mixpanelReady = false;
  private ga: Analytics | null = null;
  private initialized = false;
  private sawFirstNavigation = false;

  constructor(private router: Router) { }

  /** Call once at app startup (AppComponent). */
  init(): void {
    if (this.initialized) { return; }
    this.initialized = true;

    const token = (environment as any).mixpanelToken;
    if (token) {
      try {
        mixpanel.init(token, {
          debug: !environment.production,
          // SPA route changes are tracked below via the Router.
          track_pageview: false,
          persistence: 'localStorage',
        });
        this.mixpanelReady = true;
      } catch (e) {
        console.warn('Mixpanel init failed', e);
      }
    }

    this.initGa();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.trackPageview(e.urlAfterRedirects || e.url));
  }

  private initGa(): void {
    try {
      const app = getApps()[0];
      const measurementId = app?.options?.measurementId;
      if (!app || !measurementId || typeof window === 'undefined') { return; }
      isSupported()
        .then((ok) => {
          if (ok) {
            // getAnalytics injects gtag.js and sends the initial page_view.
            this.ga = getAnalytics(app);
          }
        })
        .catch(() => { /* analytics unsupported (privacy mode, tests) */ });
    } catch (e) {
      console.warn('GA4 init failed', e);
    }
  }

  /** Replace long id-like path segments so routes group cleanly in reports. */
  static normalizePath(url: string): string {
    const path = (url || '').split('?')[0].split('#')[0];
    return path
      .split('/')
      .map((seg) => (/^[A-Za-z0-9_-]{16,}$/.test(seg) ? ':id' : seg))
      .join('/') || '/';
  }

  private trackPageview(url: string): void {
    const routePath = AnalyticsService.normalizePath(url);
    if (this.mixpanelReady) {
      try { mixpanel.track_pageview({ route: routePath }); } catch { /* noop */ }
    }
    // GA4 sends its own page_view when gtag loads; skip the first navigation.
    if (this.sawFirstNavigation && this.ga) {
      try {
        logEvent(this.ga, 'page_view', {
          page_path: routePath,
          page_location: window.location.href,
          page_title: document.title,
        });
      } catch { /* noop */ }
    }
    this.sawFirstNavigation = true;
  }

  track(event: AnalyticsEvent, props: AnalyticsProps = {}): void {
    const clean = AnalyticsService.clean(props);
    if (this.mixpanelReady) {
      try { mixpanel.track(event, clean); } catch { /* noop */ }
    }
    const gaName = GA4_EVENT_NAMES[event];
    if (gaName && this.ga) {
      try { logEvent(this.ga, gaName, AnalyticsService.toGaParams(clean)); } catch { /* noop */ }
    }
  }

  /**
   * Track an event at most once per browser for a given key (e.g. a jobId),
   * so re-opening a job page does not double count outcomes.
   */
  trackOnce(key: string, event: AnalyticsEvent, props: AnalyticsProps = {}): boolean {
    const onceKey = `${event}:${key}`;
    let seen: string[] = [];
    try { seen = JSON.parse(localStorage.getItem(TRACK_ONCE_KEY) || '[]'); } catch { seen = []; }
    if (!Array.isArray(seen)) { seen = []; }
    if (seen.includes(onceKey)) { return false; }
    seen.push(onceKey);
    try { localStorage.setItem(TRACK_ONCE_KEY, JSON.stringify(seen.slice(-TRACK_ONCE_MAX))); } catch { /* noop */ }
    this.track(event, props);
    return true;
  }

  identify(userId: string, profile: AnalyticsProps = {}): void {
    if (!userId) { return; }
    if (this.mixpanelReady) {
      try {
        mixpanel.identify(userId);
        const clean = AnalyticsService.clean(profile);
        if (Object.keys(clean).length) { mixpanel.people.set(clean); }
      } catch { /* noop */ }
    }
    if (this.ga) {
      try { setUserId(this.ga, userId); } catch { /* noop */ }
    }
  }

  setPeople(props: AnalyticsProps): void {
    if (!this.mixpanelReady) { return; }
    try { mixpanel.people.set(AnalyticsService.clean(props)); } catch { /* noop */ }
  }

  setPeopleOnce(props: AnalyticsProps): void {
    if (!this.mixpanelReady) { return; }
    try { mixpanel.people.set_once(AnalyticsService.clean(props)); } catch { /* noop */ }
  }

  private static clean(props: AnalyticsProps): Record<string, string | number | boolean | null> {
    const out: Record<string, string | number | boolean | null> = {};
    Object.keys(props || {}).forEach((k) => {
      const v = props[k];
      if (v !== undefined) { out[k] = v; }
    });
    return out;
  }

  private static toGaParams(props: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    Object.keys(props).forEach((k) => {
      const v = props[k];
      if (v === null || v === undefined) { return; }
      const key = k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9_]/g, '_').toLowerCase().slice(0, 40);
      out[key] = typeof v === 'string' ? v.slice(0, 100) : v;
    });
    return out;
  }
}
